import React from "react";
import { AbsoluteFill, Sequence, useCurrentFrame, interpolate, spring } from "remotion";
import { COLORS, FONTS, SPRING, FPS, SCENE } from "./constants";
import { Backdrop, SlideChip, SlideTitle, Card3D, Punchline, glow } from "../EAKCLearning/shared";

const TOTAL = 7; // numbered content scenes

const appear = (frame: number, delay = 0, dur = 16) =>
  interpolate(frame, [delay, delay + dur], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

// ── small building blocks ───────────────────────────────────────────────────
const FlowNode: React.FC<{
  x: number; y: number; w: number; h: number; color: string;
  title: string; sub?: string; frame: number; delay: number; big?: boolean;
}> = ({ x, y, w, h, color, title, sub, frame, delay, big }) => {
  const sp = spring({ frame: frame - delay, fps: FPS, config: SPRING.bouncy });
  return (
    <div style={{
      position: "absolute", left: x, top: y, width: w, height: h,
      opacity: sp,
      transform: `scale(${interpolate(sp, [0, 1], [0.6, 1])})`,
      background: `linear-gradient(140deg, ${color}22, ${COLORS.bgCard}f2)`,
      border: `2.5px solid ${color}`, borderRadius: 18,
      boxShadow: `0 0 34px ${color}55, 0 14px 30px rgba(0,0,0,.5)`,
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: 16, textAlign: "center",
    }}>
      <div style={{ fontSize: big ? 30 : 24, fontWeight: 900, color: COLORS.textPrimary, fontFamily: FONTS.heading, textShadow: glow(color), lineHeight: 1.1 }}>{title}</div>
      {sub && <div style={{ fontSize: 17, color: COLORS.textSecondary, marginTop: 8, fontFamily: FONTS.mono, lineHeight: 1.25 }}>{sub}</div>}
    </div>
  );
};

const Arrow: React.FC<{ x: number; y: number; w: number; color: string; frame: number; delay: number; label?: string }> = ({ x, y, w, color, frame, delay, label }) => {
  const p = interpolate(frame, [delay, delay + 14], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <div style={{ position: "absolute", left: x, top: y, width: w, height: 4, opacity: p }}>
      <div style={{ width: `${p * 100}%`, height: 4, background: color, borderRadius: 4, boxShadow: glow(color) }} />
      <div style={{ position: "absolute", right: 0, top: -8, width: 0, height: 0, borderTop: "10px solid transparent", borderBottom: "10px solid transparent", borderLeft: `14px solid ${color}`, opacity: p }} />
      {label && <div style={{ position: "absolute", top: -34, left: "50%", transform: "translateX(-50%)", fontSize: 15, color, fontFamily: FONTS.mono, fontWeight: 700, whiteSpace: "nowrap", textShadow: glow(color) }}>{label}</div>}
    </div>
  );
};

const Chip: React.FC<{ text: string; color: string; frame: number; delay: number }> = ({ text, color, frame, delay }) => {
  const sp = spring({ frame: frame - delay, fps: FPS, config: SPRING.snappy });
  return (
    <div style={{
      opacity: sp, transform: `scale(${interpolate(sp, [0, 1], [0.7, 1])})`,
      padding: "16px 26px", borderRadius: 16, background: `${color}18`, border: `2px solid ${color}`,
      fontSize: 24, fontWeight: 800, color, fontFamily: FONTS.mono, textShadow: glow(color), whiteSpace: "nowrap",
    }}>{text}</div>
  );
};

const Bubble: React.FC<{ x: number; y: number; who: "u" | "a"; text: string; frame: number; delay: number }> = ({ x, y, who, text, frame, delay }) => {
  const sp = spring({ frame: frame - delay, fps: FPS, config: SPRING.gentle });
  const c = who === "u" ? COLORS.neonBlue : COLORS.neonPurple;
  return (
    <div style={{
      position: "absolute", left: x, top: y, maxWidth: 520,
      opacity: sp, transform: `translateX(${interpolate(sp, [0, 1], [who === "u" ? -30 : 30, 0])}px)`,
      background: `${c}18`, border: `2px solid ${c}`, borderRadius: 16,
      padding: "12px 18px", fontSize: 20, color: COLORS.textPrimary, fontFamily: FONTS.body,
    }}>
      <span style={{ color: c, fontFamily: FONTS.mono, fontWeight: 800, marginRight: 8 }}>{who === "u" ? "you" : "agent"}</span>
      {text}
    </div>
  );
};

// ── scenes ──────────────────────────────────────────────────────────────────
const Cold: React.FC = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={COLORS.neonRed} dark />
      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        <div style={{ fontSize: 92, fontWeight: 900, fontFamily: FONTS.heading, color: COLORS.textPrimary, opacity: appear(f, 0), textShadow: `0 0 50px ${COLORS.neonRed}` }}>Your AI forgets</div>
        <div style={{ fontSize: 92, fontWeight: 900, fontFamily: FONTS.heading, color: COLORS.neonRed, opacity: appear(f, 26), textShadow: glow(COLORS.neonRed, true) }}>everything you told it.</div>
        <div style={{ fontSize: 34, color: COLORS.textMuted, marginTop: 26, fontFamily: FONTS.body, opacity: appear(f, 64) }}>the moment the chat ends.</div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const Title: React.FC = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={COLORS.neonBlue} />
      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        <div style={{ fontSize: 82, fontWeight: 900, fontFamily: FONTS.heading, color: COLORS.textPrimary, textAlign: "center", opacity: appear(f, 0), textShadow: `0 0 50px ${COLORS.neonBlue}dd` }}>The Memory & Learning Loop</div>
        <div style={{ display: "flex", gap: 18, marginTop: 30, opacity: appear(f, 40) }}>
          {["session_summarizer", "episode_summarizer"].map((t, i) => (
            <div key={t} style={{ padding: "12px 26px", borderRadius: 30, border: `2px solid ${i ? COLORS.neonGreen : COLORS.neonCyan}`, color: i ? COLORS.neonGreen : COLORS.neonCyan, fontFamily: FONTS.mono, fontSize: 26, fontWeight: 800, textShadow: glow(i ? COLORS.neonGreen : COLORS.neonCyan) }}>{t}</div>
          ))}
        </div>
        <div style={{ fontSize: 24, color: COLORS.textMuted, marginTop: 26, fontFamily: FONTS.body, opacity: appear(f, 70) }}>how EAKC turns conversations into memory — and new behaviour</div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

const Problem: React.FC = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={COLORS.neonOrange} />
      <SlideChip n={1} total={TOTAL} color={COLORS.neonOrange} appear={appear(f, 0)} />
      <SlideTitle text="Stateless by default" subtitle="a raw LLM is an amnesiac" color={COLORS.neonOrange} appear={appear(f, 6)} />
      {[
        { t: "Forgets context", s: "every chat starts from zero", c: COLORS.neonRed, x: 300 },
        { t: "Never learns", s: "your corrections don't stick", c: COLORS.neonPink, x: 1080 },
      ].map((o, i) => (
        <Card3D key={o.t} x={o.x} y={340} w={540} h={280} color={o.c} frame={f} delay={24 + i * 12} fps={FPS}>
          <div style={{ fontSize: 40 }}>✕</div>
          <div style={{ fontSize: 40, fontWeight: 900, color: COLORS.textPrimary, fontFamily: FONTS.heading, marginTop: 8, textShadow: glow(o.c) }}>{o.t}</div>
          <div style={{ fontSize: 24, color: COLORS.textSecondary, marginTop: 12, fontFamily: FONTS.body }}>{o.s}</div>
        </Card3D>
      ))}
      <Punchline text="Every morning it starts from zero." color={COLORS.neonOrange} appear={appear(f, 80)} />
    </AbsoluteFill>
  );
};

const Stage1: React.FC = () => {
  const f = useCurrentFrame();
  const c = COLORS.neonCyan;
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={c} />
      <SlideChip n={2} total={TOTAL} color={c} appear={appear(f, 0)} />
      <SlideTitle text="① Session Summarizer" subtitle="idle chat → a durable episode" color={c} appear={appear(f, 6)} />
      <FlowNode x={140} y={430} w={360} h={200} color={COLORS.neonBlue} title="Chat · 20 turns" sub="the full transcript" frame={f} delay={24} />
      <Arrow x={520} y={528} w={220} color={c} frame={f} delay={38} label="distill" />
      <FlowNode x={770} y={430} w={360} h={200} color={c} title="Episode" sub="the signal, not the transcript" frame={f} delay={52} big />
      <Arrow x={1150} y={528} w={200} color={COLORS.neonGreen} frame={f} delay={66} label="store" />
      <FlowNode x={1380} y={430} w={360} h={200} color={COLORS.neonGreen} title="Long-term memory" sub="agent_episodes · tier-3" frame={f} delay={80} />
      <div style={{ position: "absolute", bottom: 150, left: 0, right: 0, textAlign: "center", opacity: appear(f, 100), fontSize: 20, color: COLORS.textMuted, fontFamily: FONTS.mono }}>
        runs when a chat has been idle ≥ 24h · only agent-attached sessions
      </div>
      <Punchline text="Keep the signal. Drop the transcript." color={c} appear={appear(f, 110)} />
    </AbsoluteFill>
  );
};

const Stage2: React.FC = () => {
  const f = useCurrentFrame();
  const c = COLORS.neonGreen;
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={c} />
      <SlideChip n={3} total={TOTAL} color={c} appear={appear(f, 0)} />
      <SlideTitle text="② Episode Summarizer" subtitle="episodes → proposed skills" color={c} appear={appear(f, 6)} size={70} />
      <FlowNode x={120} y={440} w={330} h={190} color={COLORS.neonBlue} title="Recent episodes" sub="last ~50" frame={f} delay={24} />
      <Arrow x={470} y={530} w={150} color={c} frame={f} delay={38} label="find patterns" />
      <FlowNode x={640} y={430} w={330} h={210} color={COLORS.neonPurple} title="A pattern" sub="preference · correction · repeated mistake" frame={f} delay={52} />
      <Arrow x={990} y={530} w={140} color={c} frame={f} delay={66} label="propose" />
      <FlowNode x={1150} y={430} w={310} h={210} color={c} title="Persona-rule skill" sub="a new behaviour" frame={f} delay={80} />
      <Arrow x={1480} y={530} w={110} color={COLORS.neonYellow} frame={f} delay={94} label="review" />
      <FlowNode x={1610} y={440} w={210} h={190} color={COLORS.neonYellow} title="/admin/learned" sub="you approve" frame={f} delay={108} />
      <Punchline text="It learns from you — with a human in the loop." color={c} appear={appear(f, 124)} />
    </AbsoluteFill>
  );
};

const ExampleA: React.FC = () => {
  const f = useCurrentFrame();
  const c = COLORS.neonBlue;
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={c} />
      <SlideChip n={4} total={TOTAL} color={c} appear={appear(f, 0)} />
      <SlideTitle text="One long conversation" subtitle="multi-turn → one episode" color={c} appear={appear(f, 6)} />
      <Bubble x={160} y={300} who="u" text="Draft our launch email…" frame={f} delay={24} />
      <Bubble x={220} y={368} who="a" text="Here's a draft — formal tone." frame={f} delay={36} />
      <Bubble x={160} y={436} who="u" text="Warmer, and cut the jargon." frame={f} delay={48} />
      <Bubble x={220} y={504} who="a" text="Reworked — warmer, plain words." frame={f} delay={60} />
      <Bubble x={160} y={572} who="u" text="Perfect, ship it." frame={f} delay={72} />
      <Arrow x={720} y={470} w={220} color={COLORS.neonGreen} frame={f} delay={92} label="distill" />
      <FlowNode x={970} y={370} w={780} h={220} color={COLORS.neonGreen} title="Episode" sub="Goal: launch email · Pref: warm, no jargon · Outcome: shipped" frame={f} delay={104} />
      <Punchline text="20 turns → 1 memory the agent can reuse." color={c} appear={appear(f, 128)} />
    </AbsoluteFill>
  );
};

const ExampleB: React.FC = () => {
  const f = useCurrentFrame();
  const c = COLORS.neonPurple;
  const chats = ["Blog post", "Sales email", "Docs page", "Slack note"];
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={c} />
      <SlideChip n={5} total={TOTAL} color={c} appear={appear(f, 0)} />
      <SlideTitle text="Many chats, one pattern" subtitle="across separate conversations" color={c} appear={appear(f, 6)} />
      {chats.map((t, i) => (
        <FlowNode key={t} x={140 + i * 210} y={330} w={190} h={150} color={COLORS.neonBlue} title={t} sub={`"add a source"`} frame={f} delay={24 + i * 10} />
      ))}
      <div style={{ position: "absolute", top: 500, left: 140, width: 830, height: 3, background: c, opacity: appear(f, 70), boxShadow: glow(c) }} />
      <div style={{ position: "absolute", top: 512, left: 0, width: 970, textAlign: "center", opacity: appear(f, 78), fontSize: 20, color: c, fontFamily: FONTS.mono, fontWeight: 700 }}>same correction, four times →</div>
      <Arrow x={990} y={405} w={150} color={COLORS.neonGreen} frame={f} delay={86} label="pattern" />
      <FlowNode x={1160} y={310} w={620} h={190} color={COLORS.neonGreen} title="New skill" sub={`Persona rule: "Always cite the source."`} frame={f} delay={100} big />
      <Punchline text="The pattern across chats becomes a skill." color={c} appear={appear(f, 122)} />
    </AbsoluteFill>
  );
};

const Cost: React.FC = () => {
  const f = useCurrentFrame();
  const c = COLORS.neonYellow;
  const items = [
    { t: "≤ 100 episodes / run", c: COLORS.neonCyan },
    { t: "1,500-token cap", c: COLORS.neonGreen },
    { t: "cheap model tier", c: COLORS.neonPurple },
    { t: "thrash guard", c: COLORS.neonPink },
  ];
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={c} />
      <SlideChip n={6} total={TOTAL} color={c} appear={appear(f, 0)} />
      <SlideTitle text="Always learning ≠ always burning" subtitle="cost-bounded on purpose" color={c} appear={appear(f, 6)} size={64} />
      <div style={{ position: "absolute", top: 480, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 30 }}>
        {items.map((o, i) => <Chip key={o.t} text={o.t} color={o.c} frame={f} delay={26 + i * 12} />)}
      </div>
      <Punchline text="Learning shouldn't cost more than what it learns from." color={c} appear={appear(f, 90)} />
    </AbsoluteFill>
  );
};

const Value: React.FC = () => {
  const f = useCurrentFrame();
  const c = COLORS.neonGreen;
  const cards = [
    { t: "Remembers", s: "context across sessions", c: COLORS.neonCyan },
    { t: "Improves", s: "from your feedback", c: COLORS.neonGreen },
    { t: "Onboards faster", s: "less repeating yourself", c: COLORS.neonPurple },
  ];
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={c} />
      <SlideChip n={7} total={TOTAL} color={c} appear={appear(f, 0)} />
      <SlideTitle text="Why it matters" subtitle="the difference between a demo and a product" color={c} appear={appear(f, 6)} />
      {cards.map((o, i) => (
        <Card3D key={o.t} x={190 + i * 540} y={360} w={480} h={280} color={o.c} frame={f} delay={24 + i * 12} fps={FPS}>
          <div style={{ fontSize: 40, fontWeight: 900, color: COLORS.textPrimary, fontFamily: FONTS.heading, textShadow: glow(o.c) }}>{o.t}</div>
          <div style={{ fontSize: 26, color: COLORS.textSecondary, marginTop: 14, fontFamily: FONTS.body }}>{o.s}</div>
        </Card3D>
      ))}
      <Punchline text="Quality that compounds — not resets." color={c} appear={appear(f, 84)} />
    </AbsoluteFill>
  );
};

const Outro: React.FC = () => {
  const f = useCurrentFrame();
  const c = COLORS.neonGreen;
  return (
    <AbsoluteFill>
      <Backdrop frame={f} accent={c} />
      <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", flexDirection: "column" }}>
        <div style={{ fontSize: 100, fontWeight: 900, fontFamily: FONTS.heading, color: COLORS.textPrimary, opacity: appear(f, 0), textShadow: `0 0 60px ${c}` }}>Distill, don&apos;t dump.</div>
        <div style={{ fontSize: 30, color: COLORS.textSecondary, marginTop: 24, fontFamily: FONTS.body, opacity: appear(f, 40) }}>EAKC — memory & learning, built in public.</div>
      </AbsoluteFill>
    </AbsoluteFill>
  );
};

export const EAKCMemoryLoop: React.FC = () => (
  <AbsoluteFill style={{ background: COLORS.bg, fontFamily: FONTS.body }}>
    <Sequence from={SCENE.cold.start} durationInFrames={SCENE.cold.duration}><Cold /></Sequence>
    <Sequence from={SCENE.title.start} durationInFrames={SCENE.title.duration}><Title /></Sequence>
    <Sequence from={SCENE.problem.start} durationInFrames={SCENE.problem.duration}><Problem /></Sequence>
    <Sequence from={SCENE.stage1.start} durationInFrames={SCENE.stage1.duration}><Stage1 /></Sequence>
    <Sequence from={SCENE.stage2.start} durationInFrames={SCENE.stage2.duration}><Stage2 /></Sequence>
    <Sequence from={SCENE.exampleA.start} durationInFrames={SCENE.exampleA.duration}><ExampleA /></Sequence>
    <Sequence from={SCENE.exampleB.start} durationInFrames={SCENE.exampleB.duration}><ExampleB /></Sequence>
    <Sequence from={SCENE.cost.start} durationInFrames={SCENE.cost.duration}><Cost /></Sequence>
    <Sequence from={SCENE.value.start} durationInFrames={SCENE.value.duration}><Value /></Sequence>
    <Sequence from={SCENE.outro.start} durationInFrames={SCENE.outro.duration}><Outro /></Sequence>
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
